import React, { useEffect, useState } from "react";
import * as auth from "@utils/auth";
import Box from "@mui/material/Box";
import DashboardHeader from "@components/DashboardHeader";
import RegisterForm from "@components/Register";
import Overlay from "@containers/Overlay";
import Notification from "@containers/Notification";
import { DataSource } from "@factory/DataSource";
import Traductor from "@utils/traductor";
import { LANGUAGE, CORPORATION } from "@/constants";
import { useGlobalContext, useGlobalContextUpdate } from "@contexts/Global";
import { checkCurrentTable } from "@/utils/tables";

const api = DataSource(auth.getEnv());
const traductor = new Traductor(LANGUAGE);

export const Register = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [record, setRecord] = useState([]);
  const globalContext = useGlobalContext();
  const setGlobalContext = useGlobalContextUpdate();
  const { data = {}, corporation = "", modalContent = "" } = globalContext;
  const {
    name = "",
    table = "",
    place = "",
    placeId = "",
    currentTable = {},
    tables = [], 
    id = "",
  } = data;

  const filterByCorporation = (candidates = []) => {
    return candidates.filter(
      (candidate) => corporation === candidate.corp.replace("á", "a")
    );
  };

  const getTableFromRecord = (result) => {
    const [record] = result;
    const { mesas: tables = [] } = record;
    const [table = {}] = tables;
    const {
      votos_partido = 0,
      votos_partido_cam = 0,
      votos_partido_consulta = 0,
      candidatos: candidates = [],
    } = table;
    return {
      votos_partido,
      votos_partido_cam,
      votos_partido_consulta,
      candidates,
    };
  };

  const getPoliticalPartyByCorporationFromRecord = () => {
    const { votos_partido, votos_partido_cam, votos_partido_consulta } =
      getTableFromRecord(record);
    const isSenado = corporation === CORPORATION.SENADO;
    const isCamara = corporation === CORPORATION.CAMARA;
    const isConsulta = corporation === CORPORATION.CONSULTA;
    let politicalParty = 0;
    if (isSenado) {
      politicalParty = votos_partido;
    }
    if (isCamara) {
      politicalParty = votos_partido_cam;
    }
    if (isConsulta) {
      politicalParty = votos_partido_consulta;
    }
    return politicalParty;
  };

  const fetchCandidates = async () => {
    setGlobalContext({ showOverlay: true });
    setIsLoading(true);
    try {
      const {
        ok = true,
        error = null,
        result = {},
      } = await api.getCandidates({ table: currentTable.mesa, placeId });
      setGlobalContext({ showOverlay: false });
      if (ok && result && result.length) {
        setRecord(result);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      console.log(error || result);
    } finally {
      // request done
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  const buildVotesPayload = (update) => {
    const {
      candidates = [],
      votos_partido,
      votos_partido_cam,
      votos_partido_consulta,
    } = getTableFromRecord(record);
    const payload = {
      votos_partido,
      votos_partido_cam,
      votos_partido_consulta,
    };
    if (update.candidates && update.candidates.length) {
      payload.candidatos = candidates.map((candidate) => {
        const updatedCandidate = update.candidates.find(
          ({ numero }) => candidate.numero === numero
        );
        if (updatedCandidate) {
          candidate.votos = updatedCandidate.votos;
        }
        return candidate;
      });
    }
    const isSenado = corporation === CORPORATION.SENADO;
    const isCamara = corporation === CORPORATION.CAMARA;
    const isConsulta = corporation === CORPORATION.CONSULTA;
    if (isSenado) {
      payload.votos_partido = update.politicalParty;
    }
    if (isCamara) {
      payload.votos_partido_cam = update.politicalParty;
    }
    if (isConsulta) {
      payload.votos_partido_consulta = update.politicalParty;
    }
    return payload;
  };

  const handleSubmit = async ({ politicalParty, fields: candidates }) => {
    const payload = buildVotesPayload({ politicalParty, candidates });
    setGlobalContext({ showOverlay: true });

    const updatedTable = checkCurrentTable({
      option: modalContent,
      currentTable,
      corporation,
    });

    try {
      const { ok = true, result = {} } = await api.setVotes(payload, {
        table: currentTable.mesa,
        placeId,
      });
      setGlobalContext({
        showOverlay: false,
        isModalOpen: false,
      });
      if (ok && result.success) {
        setGlobalContext({
          isModalOpen: false,
          data: {
            ...globalContext.data,
            currentTable: updatedTable,
          },
        });
        const isTableReported =
          currentTable.E14_sen &&
          currentTable.cuenta_votos_sen &&
          currentTable.ingreso_datos_sen &&
          currentTable.E14_consulta &&
          currentTable.cuenta_votos_consulta &&
          currentTable.ingreso_datos_consulta &&
          currentTable.E14_cam &&
          currentTable.cuenta_votos_cam &&
          currentTable.ingreso_datos_cam;

        if (isTableReported) {
          const { result = null } = await api.reportTable({
            placeId,
            mesa: currentTable.mesa,
          });
          if (result && result.success === false) {
            return setGlobalContext({
              notificationType: "error",
              notificationMessage: traductor.translate("error"),
              showNotification: true,
            });
          }
        }
        const updateUserTableResponse = await api.updateUserTable(id, {
          check_mesas: tables,
        });
        if (
          updateUserTableResponse.ok &&
          updateUserTableResponse.result.success
        ) {
          return setGlobalContext({
            notificationType: "success",
            notificationMessage: traductor.translate("registerComplete"),
            showNotification: true,
            showOverlay: false,
            isModalOpen: false,
          });
        }
        setGlobalContext({
          notificationType: "error",
          notificationMessage: traductor.translate("error"),
          showNotification: true,
        });
      }
    } finally {
      // request done
    }
  };

  return (
    <Box>
      <DashboardHeader
        name={name}
        place={place}
        table={table}
        corporation={corporation}
        corporationFixed={true}
      />

      {!isLoading && record.length && (
        <RegisterForm
          onSubmit={handleSubmit}
          politicalParty={getPoliticalPartyByCorporationFromRecord()}
          corporation={corporation}
          fields={filterByCorporation(getTableFromRecord(record).candidates)}
          labels={{
            politicalParty: traductor.translate("politicalParty"),
            invalidField: traductor.translate("invalidField"),
            save: traductor.translate("save"),
          }}
        />
      )}
      <Overlay />
      <Notification />
    </Box>
  );
};
